import { ArrowRight, GitFork, RefreshCw, ShieldCheck } from 'lucide-react';
import type { UseQueryResult } from '@tanstack/react-query';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useNow } from '@/hooks/useChainData';
import {
  CHAINS,
  formatInt,
  shortHash,
  timeAgo,
  type BlockInfo,
  type ForkStatus,
} from '@/lib/mempool';

function ChainTip({
  chainId,
  tip,
  alignRight = false,
}: {
  chainId: 'standard' | 'bip110';
  tip?: BlockInfo;
  alignRight?: boolean;
}) {
  const chain = CHAINS[chainId];
  const now = useNow();

  return (
    <div className={`min-w-0 flex-1 space-y-1.5 ${alignRight ? 'md:text-right' : ''}`}>
      <div className={`flex items-center gap-2 ${alignRight ? 'md:justify-end' : ''}`}>
        <span className={`size-2 rounded-full ${chain.classes.dot}`} aria-hidden />
        <span className="text-sm font-medium text-muted-foreground">
          {chain.name} <span className={chain.classes.text}>({chain.shortName})</span>
        </span>
      </div>
      {tip ? (
        <>
          <div className={`font-mono text-4xl font-bold tracking-tight sm:text-5xl ${chain.classes.text}`}>
            {formatInt(tip.height)}
          </div>
          <div className="font-mono text-xs text-muted-foreground">
            {shortHash(tip.id, 12, 8)} · {timeAgo(tip.timestamp, now)}
          </div>
        </>
      ) : (
        <>
          <Skeleton className={`h-10 w-40 sm:h-12 ${alignRight ? 'md:ml-auto' : ''}`} />
          <Skeleton className={`h-3.5 w-48 ${alignRight ? 'md:ml-auto' : ''}`} />
        </>
      )}
    </div>
  );
}

function ForkStatusLine({ query }: { query: UseQueryResult<ForkStatus> }) {
  const { data, isPending, isError } = query;

  if (isPending) {
    return <Skeleton className="h-7 w-full max-w-xl" />;
  }

  if (isError || !data) {
    return (
      <p className="text-xs text-muted-foreground">
        Fork comparison temporarily unavailable (rate limit or network issue).
      </p>
    );
  }

  if (data.match) {
    return (
      <div className="flex items-start gap-2 rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-3.5 py-2.5 text-sm text-emerald-700 dark:text-emerald-400">
        <ShieldCheck className="mt-0.5 size-4 shrink-0" />
        <span>
          Both chains agree on the block at height{' '}
          <span className="font-mono font-medium">{formatInt(data.comparedHeight)}</span> — sharing
          common history up to the lower tip.
        </span>
      </div>
    );
  }

  return (
    <div className="flex items-start gap-2 rounded-lg border border-orange-500/40 bg-orange-500/10 px-3.5 py-2.5 text-sm text-orange-700 dark:text-orange-400">
      <GitFork className="mt-0.5 size-4 shrink-0" />
      <span>
        <span className="font-semibold">Chains have diverged.</span> Different blocks at height{' '}
        <span className="font-mono font-medium">{formatInt(data.comparedHeight)}</span>: Standard{' '}
        <span className="font-mono">{shortHash(data.standardHash, 6, 4)}</span> vs BIP-110{' '}
        <span className="font-mono">{shortHash(data.bip110Hash, 6, 4)}</span>.
      </span>
    </div>
  );
}

export function ComparisonHeader({
  standardTip,
  bip110Tip,
  forkQuery,
  isFetching,
  updatedAt,
  onRefresh,
}: {
  standardTip?: BlockInfo;
  bip110Tip?: BlockInfo;
  forkQuery: UseQueryResult<ForkStatus>;
  isFetching: boolean;
  updatedAt: number;
  onRefresh: () => void;
}) {
  const now = useNow();

  const delta =
    standardTip && bip110Tip ? standardTip.height - bip110Tip.height : undefined;

  return (
    <div className="relative isolate overflow-hidden rounded-2xl border bg-card/60">
      {/* Backdrop accents */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 -z-10 bg-gradient-to-r from-amber-500/10 via-transparent to-violet-500/10"
      />

      <div className="flex items-center justify-between gap-3 px-5 pt-4 sm:px-6">
        <p className="text-xs font-medium tracking-widest text-muted-foreground uppercase">
          Live chain telemetry
        </p>
        <div className="flex items-center gap-3">
          {updatedAt > 0 && (
            <span className="hidden font-mono text-xs text-muted-foreground sm:inline">
              updated {timeAgo(Math.floor(updatedAt / 1000), now)}
            </span>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={onRefresh}
            disabled={isFetching}
            className="gap-1.5"
          >
            <RefreshCw className={`size-3.5 ${isFetching ? 'motion-safe:animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      <div className="flex flex-col gap-6 px-5 py-6 sm:px-6 md:flex-row md:items-center md:gap-4">
        <ChainTip chainId="standard" tip={standardTip} />

        <div className="flex shrink-0 flex-col items-center gap-2 md:px-4">
          <span className="rounded-full border bg-background/60 px-3 py-1 font-mono text-xs font-semibold tracking-widest text-muted-foreground">
            VS
          </span>
          {delta !== undefined && (
            <Badge
              variant="outline"
              className={
                delta === 0
                  ? 'border-emerald-500/30 bg-emerald-500/10 font-mono text-emerald-700 dark:text-emerald-400'
                  : delta > 0
                    ? 'border-amber-500/30 bg-amber-500/10 font-mono text-amber-700 dark:text-amber-400'
                    : 'border-violet-500/30 bg-violet-500/10 font-mono text-violet-700 dark:text-violet-400'
              }
            >
              {delta === 0 ? (
                'height tied'
              ) : (
                <>
                  {delta > 0 ? 'Standard' : 'BIP-110'} +{formatInt(Math.abs(delta))}
                  <ArrowRight className="size-3" />
                </>
              )}
            </Badge>
          )}
        </div>

        <ChainTip chainId="bip110" tip={bip110Tip} alignRight />
      </div>

      <div className="px-5 pb-5 sm:px-6">
        <ForkStatusLine query={forkQuery} />
      </div>
    </div>
  );
}
